import { checkedAddressSchema } from '@sparkdotfi/common-universal'
import {
  createRootRoute,
  createRoute,
  createRouter,
  notFound,
  redirect,
  retainSearchParams,
} from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import { z } from 'zod'

import { sparkDomainSchema, supportedChainIdSchema } from '../domain/common/validation'
import { NotFoundComponent } from '../features/errors/NotFoundComponent'
import { EasyBorrowPage } from '../pages/Borrow'
import { MarketDetails } from '../pages/MarketDetails'
import { Markets } from '../pages/Markets'
import { MyPortfolioPage } from '../pages/MyPortfolio'
import { Points } from '../pages/Points'
import { RootRoute } from '../pages/RootRoute'
import { Savings } from '../pages/Savings'
import { SparkRewards } from '../pages/SparkRewards'
import { SpkAirdrop } from '../pages/SpkAirdrop'
import { SpkFarm } from '../pages/SpkFarm'
import { SpkGovernance } from '../pages/SpkGovernance'
import { SpkStaking } from '../pages/SpkStaking'
import { SwapPage } from '../pages/swap/Swap'
import { SupportedChainId } from '@/domain/types/SupportedChainId'
import { links } from '@/ui/constants/links'

export type UrlInjectedSearch = z.infer<typeof urlInjectedSearchSchema>
type UrlInjectedSearchKeys = keyof UrlInjectedSearch
const urlInjectedSearchSchema = z.object({
  ref_code: fallback(z.union([z.string(), z.number()]).optional(), ''),
  ref: fallback(z.union([z.string(), z.number()]).optional(), ''),
  chainId: fallback(
    z
      .number()
      .transform((value) => SupportedChainId(value))
      .optional(),
    undefined,
  ),
  rpc: fallback(z.string().optional(), ''),
  utm_source: fallback(z.union([z.string(), z.number()]).optional(), ''),
  'watch-address': fallback(checkedAddressSchema.optional(), undefined),
})

const rootRoute = createRootRoute({
  notFoundComponent: NotFoundComponent,
  component: RootRoute,
  validateSearch: zodValidator(urlInjectedSearchSchema),
  search: {
    middlewares: [retainSearchParams(Object.keys(urlInjectedSearchSchema.shape) as UrlInjectedSearchKeys[])],
  },
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Savings,
})

const easyBorrowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/borrow',
  component: EasyBorrowPage,
})

const myPortfolioRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-portfolio',
  component: MyPortfolioPage,
})

const savingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/savings/$domain/$savingsToken',
  component: Savings,
  params: {
    parse: (params) =>
      parseParams({
        params,
        schema: z.object({
          domain: sparkDomainSchema,
          savingsToken: z.string(),
        }),
      }),
  },
})

const marketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/markets',
  component: Markets,
})

const marketDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/markets/$chainId/$asset',
  component: MarketDetails,
  validateSearch: zodValidator(
    z.object({
      'usdc-proxy-market': fallback(z.boolean().optional(), false),
    }),
  ),
  params: {
    parse: (params) =>
      parseParams({
        params,
        schema: z.object({
          chainId: supportedChainIdSchema,
          asset: checkedAddressSchema,
        }),
      }),
  },
})

const pointsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/points/{-$referralCode}',
  component: Points,
  params: {
    parse: (params) =>
      parseParams({
        params,
        schema: z.object({
          referralCode: z
            .string()
            .optional()
            .transform((value) => value?.toUpperCase()),
        }),
      }),
  },
})

const sparkRewardsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rewards',
  component: SparkRewards,
})

const spkStakingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spk/staking',
  component: SpkStaking,
})

const spkAirdropRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spk/airdrop',
  component: SpkAirdrop,
})

const spkFarmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spk/farm',
  component: SpkFarm,
})

const spkGovernanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/spk/governance',
  component: SpkGovernance,
})

const swapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/swap',
  component: SwapPage,
})

const legacyRoutes = [
  { legacyPath: '/dashboard', newPath: '/my-portfolio' },
  { legacyPath: '/savings', newPath: '/' },
  { legacyPath: '/market-details/$chainId/$asset', newPath: '/markets/$chainId/$asset' },
  {
    legacyPath: '/farm-details/$chainId/0x0650CAF159C5A49f711e8169D4336ECB9b950275',
    newUrl: links.appSky.farms.usdsToSky,
  },
  {
    legacyPath: '/farm-details/$chainId/0x10ab606B067C9C461d8893c47C7512472E19e2Ce',
    newUrl: links.appSky.farms.usdsToClePoints,
  },
  { legacyPath: '/farm-details/$chainId/$address', newUrl: links.appSky.farms.all },
  { legacyPath: '/farms', newUrl: links.appSky.farms.all },
  { legacyPath: '/farms/$chainId/0x0650CAF159C5A49f711e8169D4336ECB9b950275', newUrl: links.appSky.farms.usdsToSky },
  {
    legacyPath: '/farms/$chainId/0x10ab606B067C9C461d8893c47C7512472E19e2Ce',
    newUrl: links.appSky.farms.usdsToClePoints,
  },
  { legacyPath: '/farms/$chainId/$address', newUrl: links.appSky.farms.all },
].map(({ legacyPath, newPath, newUrl }) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: legacyPath,
    beforeLoad: ({ search, params }) => {
      if (newUrl) {
        throw redirect({ href: newUrl })
      }

      if (newPath) {
        throw redirect({ to: newPath, search, params })
      }

      throw notFound()
    },
  }),
)

const routeTree = rootRoute.addChildren([
  indexRoute,
  easyBorrowRoute,
  myPortfolioRoute,
  savingsRoute,
  marketsRoute,
  marketDetailsRoute,
  pointsRoute,
  sparkRewardsRoute,
  spkStakingRoute,
  spkAirdropRoute,
  spkFarmRoute,
  spkGovernanceRoute,
  swapRoute,
  ...legacyRoutes,
])

export type Route = keyof (typeof router)['routesByPath']
export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundComponent,
})

function parseParams<T extends z.ZodTypeAny>({
  params,
  schema,
}: {
  params: Record<string, unknown>
  schema: T
}): z.output<T> {
  const result = schema.safeParse(params)
  if (!result.success) {
    throw notFound()
  }
  return result.data
}
